import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ArticleTable from './components/ArticleTable'
import { Article } from './components/ArticleForm'
import Button from '../../components/common/Button'
import Loading from '../../components/common/Loading'
import ErrorMessage from '../../components/common/ErrorMessage'
import api from '../../services/api'
import { useDebounce } from '../../hooks/useDebounce'
import './News.css'

function News() {
  const navigate = useNavigate()
  const [articles, setArticles] = useState<Article[]>([])
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | Article['status']>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  useEffect(() => {
    loadArticles()
  }, [])

  useEffect(() => {
    filterArticles()
  }, [articles, statusFilter, debouncedSearchQuery])

  const loadArticles = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await api.get<{ data: Article[], pagination: any }>('/articles?limit=500')
      setArticles(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des articles')
    } finally {
      setLoading(false)
    }
  }

  const filterArticles = () => {
    let filtered = articles

    if (statusFilter !== 'all') {
      filtered = filtered.filter((article) => article.status === statusFilter)
    }

    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase()
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.excerpt.toLowerCase().includes(query) ||
          article.slug.toLowerCase().includes(query)
      )
    }

    setFilteredArticles(filtered)
  }

  const handleCreate = () => {
    navigate('/admin/news/new')
  }

  const handleEdit = (article: Article) => {
    navigate(`/admin/news/${article.id}/edit`)
  }

  const handleDelete = async (article: Article) => {
    try {
      await api.delete(`/articles/${article.id}`)
      setArticles((prev) => prev.filter((a) => a.id !== article.id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    }
  }

  const getStatusCount = (status: Article['status']) => {
    return articles.filter((a) => a.status === status).length
  }

  if (loading) {
    return <Loading />
  }

  return (
    <div className="news-module">
      <div className="module-header">
        <div>
          <h1>Gestion des actualités</h1>
          <p className="module-description">
            Gérez les articles de votre site web
          </p>
        </div>
        <Button onClick={handleCreate}>Nouvel article</Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="news-filters">
        <div className="filter-group">
          <label htmlFor="search">Rechercher :</label>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
              id="search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Titre, extrait ou slug..."
              className="search-input"
            />
            {searchQuery !== debouncedSearchQuery && (
              <span style={{ marginLeft: '8px', fontSize: '14px', color: '#6b7280' }}>
                Recherche...
              </span>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Filtrer par statut :</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="status-filter"
          >
            <option value="all">Tous ({articles.length})</option>
            <option value="draft">Brouillons ({getStatusCount('draft')})</option>
            <option value="published">Publiés ({getStatusCount('published')})</option>
            <option value="archived">Archivés ({getStatusCount('archived')})</option>
          </select>
        </div>
      </div>

      <div className="news-stats">
        <div className="stat-card">
          <div className="stat-value">{articles.length}</div>
          <div className="stat-label">Total articles</div>
        </div>
        <div className="stat-card stat-published">
          <div className="stat-value">{getStatusCount('published')}</div>
          <div className="stat-label">Publiés</div>
        </div>
        <div className="stat-card stat-draft">
          <div className="stat-value">{getStatusCount('draft')}</div>
          <div className="stat-label">Brouillons</div>
        </div>
        <div className="stat-card stat-archived">
          <div className="stat-value">{getStatusCount('archived')}</div>
          <div className="stat-label">Archivés</div>
        </div>
      </div>

      <ArticleTable
        articles={filteredArticles}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  )
}

export default News
